import { useState, useMemo } from 'react';
import {
  Pane, Heading, Button, Badge,
  Text, TextInputField,
  PlusIcon, CalendarIcon, toaster,
  Tablist, Tab, Avatar, TrashIcon,
} from 'evergreen-ui';

import TurniDayDetailModal from './Turni/TurniDayDetailModal';
import TurniShiftModal from './Turni/TurniShiftModal';
import TurniFerieRequestModal from './Turni/TurniFerieRequestModal';
import TurniManagerFerieModal from './Turni/TurniManagerFerieModal';
import VistaCompatta from './Turni/Views/VistaCompatta';
import VistaMensile from './Turni/Views/VistaMensile';
import VistaSettimanale from './Turni/Views/VistaSettimanale';
import useWindowSize from '../utils/useWindowSize';
import useTurni from './Turni/useTurni';
import useFerie from './Turni/useFerie';

export default function TurniOperatori({ isDirector, isEducator, isOperator }) {
  const isMobile = useWindowSize() < 768;
  const [isDayModalOpen, setIsDayModalOpen] = useState(false);
  const [isTurnoModalOpen, setIsTurnoModalOpen] = useState(false);
  const [isFerieModalOpen, setIsFerieModalOpen] = useState(false);
  const [isManagerFerieOpen, setIsManagerFerieOpen] = useState(false);
  const [selectedDay, setSelectedDay] = useState(null);
  const [selectedWeek, setSelectedWeek] = useState(0);
  const [showOnlyUncovered, setShowOnlyUncovered] = useState(false);
  const [viewMode, setViewMode] = useState('mensile');
  const [isDragOverTrash, setIsDragOverTrash] = useState(false);

  const {
    userName,
    selectedMonth,
    setSelectedMonth,
    turniData,
    collaboratorsList,
    shiftInputs,
    setShiftInputs,
    loadData,
    handleSaveTurno,
    handleDeleteTurno,
    handleTimbra,
    handleDragStart,
    handleDrop,
  } = useTurni({ isDirector, isEducator });

  const {
    managerFerieList,
    ferieInputs,
    setFerieInputs,
    handleRequestFerie,
    handleOpenManagerFerie,
    handleOpenMyFerie,
    handleUpdateFerieStatus,
  } = useFerie({
    isDirector,
    userName,
    onFerieApproved: loadData,
  });

  // ---- Helper functions (pure — non dipendono da stato async) ----

  const getDaysInMonth = (monthStr) => {
    const [year, month] = monthStr.split('-');
    return new Date(year, month, 0).getDate();
  };

  const displayNameMap = useMemo(() => {
    const allNames = new Set();
    collaboratorsList.forEach(c => {
      const full = `${c.nome || ''} ${c.cognome || ''}`.trim();
      if (full) allNames.add(full);
    });
    turniData.forEach(t => { if (t.operator_name) allNames.add(t.operator_name.trim()); });
    managerFerieList.forEach(l => { if (l.operator_name) allNames.add(l.operator_name.trim()); });

    const byFirstName = {};
    for (const name of allNames) {
      const firstName = name.split(/\s+/)[0];
      if (!byFirstName[firstName]) byFirstName[firstName] = [];
      byFirstName[firstName].push(name);
    }

    const map = new Map();
    for (const [firstName, names] of Object.entries(byFirstName)) {
      if (names.length === 1) {
        map.set(names[0], firstName);
      } else {
        for (const name of names) {
          const surname = name.trim().split(/\s+/).slice(1).join(' ');
          let displayName = `${firstName} ${surname}`;
          for (let i = 1; i <= surname.length; i++) {
            const prefix = surname.substring(0, i);
            const isUnique = names
              .filter(n => n !== name)
              .every(n => !n.trim().split(/\s+/).slice(1).join(' ').startsWith(prefix));
            if (isUnique) {
              displayName = `${firstName} ${prefix}`;
              break;
            }
          }
          map.set(name, displayName);
        }
      }
    }
    return map;
  }, [collaboratorsList, turniData, managerFerieList]);

  const getInitials = (fullName) => {
    if (!fullName) return '';
    const trimmed = fullName.trim();
    return displayNameMap.get(trimmed) || trimmed.split(/\s+/)[0];
  };

  const daysCount = getDaysInMonth(selectedMonth);

  const getFirstDayIndex = (monthStr) => {
    const [year, month] = monthStr.split('-');
    let day = new Date(year, month - 1, 1).getDay();
    return day === 0 ? 6 : day - 1;
  };
  const firstDayIndex = getFirstDayIndex(selectedMonth);
  const paddingDays = Array.from({ length: firstDayIndex }, () => null);

  const actualDays = Array.from({ length: daysCount }, (_, i) => {
    const dateStr = `${selectedMonth}-${String(i + 1).padStart(2, '0')}`;
    const dayShifts = turniData.filter((t) => t.date === dateStr);
    const standardShifts = dayShifts.filter(t => t.shift_type !== 'ferie' && t.shift_type !== 'malattia' && t.shift_type !== 'permesso');
    const ferie = dayShifts.filter(t => t.shift_type === 'ferie');

    let totalHours = 0;
    standardShifts.forEach(shift => {
      if (shift.start_time && shift.end_time) {
        const [sH, sM] = shift.start_time.split(':').map(Number);
        const [eH, eM] = shift.end_time.split(':').map(Number);
        let duration = (eH + eM / 60) - (sH + sM / 60);
        if (eH === 23 && eM >= 58 && sH !== 23) duration += (24 - (eH + eM / 60));
        if (duration < 0) duration += 24;
        totalHours += duration;
      }
    });

    let status = 'coperto';
    if (totalHours === 0 && standardShifts.length === 0) status = 'scoperto';
    else if (totalHours < 24) status = 'parziale';

    return {
      date: i + 1,
      fullDate: dateStr,
      status,
      totalHours,
      shifts: {
        standard: standardShifts.map(t => t.operator_name),
        ferie: ferie.map(t => t.operator_name),
        raw: dayShifts,
      },
    };
  });

  const mockDays = paddingDays.concat(actualDays);

  const getStatusColor = (status) => {
    switch (status) {
      case 'coperto': return 'green';
      case 'parziale': return 'yellow';
      case 'scoperto': return 'red';
      default: return 'neutral';
    }
  };

  const getBadgeProps = (type) => {
    switch (type) {
      case 'ferie': return { color: 'green', text: 'F' };
      case 'permesso': return { color: 'yellow', text: 'P' };
      case 'malattia': return { color: 'red', text: 'M' };
      default: return { color: 'blue', text: 'T' };
    }
  };

  const handleDayClick = (day) => {
    setSelectedDay(day);
    setIsDayModalOpen(true);
  };

  // Statistiche di copertura mensile
  const expectedMonthHours = actualDays.length * 24;
  let coveredMonthHours = 0;
  actualDays.forEach(day => { coveredMonthHours += Math.min(day.totalHours || 0, 24); });
  const coveragePercentage = expectedMonthHours > 0 ? Math.round((coveredMonthHours / expectedMonthHours) * 100) : 0;

  // Handler drag-and-drop che apre il modale
  const onDrop = (e, dateStr) => {
    const droppedName = handleDrop(e, dateStr);
    if (droppedName) setIsTurnoModalOpen(true);
  };

  const handleShiftDragStart = (e, shiftId) => {
    e.dataTransfer.setData('shiftId', String(shiftId));
  };

  const handleTrashDrop = async (e) => {
    e.preventDefault();
    setIsDragOverTrash(false);
    const shiftId = e.dataTransfer.getData('shiftId');
    if (!shiftId) return;
    try {
      await handleDeleteTurno(Number(shiftId));
    } catch (_e) { /* toaster già mostrato in handleDeleteTurno */ }
  };

  return (
    <Pane padding={24} maxWidth={1600} marginX="auto" width="100%">

      {/* 1. HEADER E CONTROLLI */}
      <Pane display="flex" flexDirection="column" gap={16} marginBottom={32}>
        <Pane display="flex" flexDirection="column" gap={16}>
          <Pane display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={16}>
            <Pane flex={1} minWidth={280}>
              <Heading size={800} color="#2a3b2c" marginBottom={12}>Gestione turni lavorativi</Heading>
              <Pane display="flex" alignItems="center" gap={16} flexWrap="wrap">
                <Badge color={coveragePercentage < 50 ? 'red' : coveragePercentage < 80 ? 'yellow' : 'green'} isSolid paddingX={8} paddingY={4} style={{ whiteSpace: 'normal', height: 'auto', lineHeight: '1.5' }}>
                  Copertura Mensile: {coveragePercentage}% ({Math.round(coveredMonthHours)}h / {expectedMonthHours}h coperte)
                </Badge>
                <TextInputField type="month" value={selectedMonth} onChange={e => setSelectedMonth(e.target.value)} marginBottom={0} />
              </Pane>
            </Pane>
          </Pane>

          <Pane width="100%" display="flex" gap={12} alignItems="center" flexWrap="wrap" justifyContent="flex-start" marginTop={8}>
            <Button intent={showOnlyUncovered ? 'danger' : 'none'} onClick={() => setShowOnlyUncovered(!showOnlyUncovered)}>
              {showOnlyUncovered ? 'Mostra tutte le giornate' : 'Mostra le giornate scoperte/parziali'}
            </Button>

            {isDirector && (
              <Button iconBefore={PlusIcon} intent="success" appearance="primary" onClick={() => setIsTurnoModalOpen(true)}>
                Inserisci
              </Button>
            )}

            <Button iconBefore={CalendarIcon} onClick={() => setIsFerieModalOpen(true)}>
              Richiedi ferie
            </Button>

            {!isDirector && (isEducator || isOperator) && (
              <Button onClick={async () => { await handleOpenMyFerie(); setIsManagerFerieOpen(true); }} intent="none">
                Visualizza richieste ferie
              </Button>
            )}

            {isDirector && (
              <Button onClick={async () => { await handleOpenManagerFerie(); setIsManagerFerieOpen(true); }} intent="warning" appearance="primary">
                Gestione ferie
              </Button>
            )}
          </Pane>
        </Pane>
      </Pane>

      {/* 2. CALENDARIO */}
      <Pane display="flex" gap={24} flexDirection={isMobile ? 'column-reverse' : 'row'}>
        {/* SIDEBAR (Collaboratori disponibili) */}
        <Pane width={isMobile ? '100%' : 250} background="white" padding={16} borderRadius={8} border="default" marginBottom={isMobile ? 0 : 0} marginTop={isMobile ? 16 : 0}>
          <Heading size={500} marginBottom={16}>Collaboratori</Heading>
          <Pane display="flex" flexDirection="column" gap={8} marginBottom={32}>
            {(collaboratorsList || []).map(u => {
              const uName = `${u.nome} ${u.cognome}`.trim();
              return (
                <Pane
                  key={u.id}
                  padding={8}
                  background="tint2"
                  borderRadius={4}
                  draggable={isDirector}
                  onDragStart={(e) => handleDragStart(e, uName)}
                  cursor={isDirector ? 'grab' : 'default'}
                  display="flex"
                  alignItems="center"
                  gap={8}
                >
                  <Avatar name={getInitials(uName)} size={24} />
                  <Text>{uName}</Text>
                </Pane>
              );
            })}
          </Pane>

          {isDirector && viewMode === 'mensile' && (
            <Pane
              marginBottom={24}
              padding={12}
              borderRadius={8}
              border={isDragOverTrash ? '2px solid #D14343' : '2px dashed #c1c4d6'}
              background={isDragOverTrash ? '#FFEAEA' : 'tint1'}
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              gap={8}
              minHeight={72}
              onDragOver={(e) => { e.preventDefault(); setIsDragOverTrash(true); }}
              onDragLeave={() => setIsDragOverTrash(false)}
              onDrop={handleTrashDrop}
            >
              <TrashIcon color={isDragOverTrash ? 'danger' : 'muted'} size={20} />
              <Text size={300} color={isDragOverTrash ? 'danger' : 'muted'}>
                {isDragOverTrash ? 'Rilascia per eliminare' : 'Trascina un turno per eliminarlo'}
              </Text>
            </Pane>
          )}

          <Heading size={500} marginBottom={16}>Legenda</Heading>
          <Pane display="flex" flexDirection="column" gap={8}>
            <Badge color="blue" isSolid>T - Turno</Badge>
            {isDirector ? (
              <>
                <Badge color="green" isSolid>F - Ferie</Badge>
                <Badge color="yellow" isSolid>P - Permesso</Badge>
                <Badge color="red" isSolid>M - Malattia</Badge>
              </>
            ) : (
              <Badge color="neutral" isSolid>ASS - Assente</Badge>
            )}
          </Pane>
        </Pane>

        {/* AREA PRINCIPALE */}
        <Pane flex={1} background="white" padding={16} borderRadius={8} border="default" overflowX="auto">
          <Tablist marginBottom={16}>
            <Tab isSelected={viewMode === 'compatta'} onSelect={() => setViewMode('compatta')}>Vista compatta</Tab>
            <Tab isSelected={viewMode === 'mensile'} onSelect={() => setViewMode('mensile')}>Vista mensile</Tab>
            {isDirector && <Tab isSelected={viewMode === 'settimanale'} onSelect={() => setViewMode('settimanale')}>Vista settimanale</Tab>}
          </Tablist>

          {viewMode === 'compatta' && (
            <VistaCompatta
              mockDays={mockDays}
              showOnlyUncovered={showOnlyUncovered}
              handleDayClick={handleDayClick}
              getStatusColor={getStatusColor}
            />
          )}

          {viewMode === 'mensile' && (
            <VistaMensile
              mockDays={mockDays}
              showOnlyUncovered={showOnlyUncovered}
              turniData={turniData}
              managerFerieList={managerFerieList}
              isDirector={isDirector}
              getInitials={getInitials}
              getBadgeProps={getBadgeProps}
              handleDrop={onDrop}
              handleDayClick={handleDayClick}
              setShiftInputs={setShiftInputs}
              shiftInputs={shiftInputs}
              setIsTurnoModalOpen={setIsTurnoModalOpen}
              onShiftDragStart={handleShiftDragStart}
            />
          )}

          {viewMode === 'settimanale' && isDirector && (
            <VistaSettimanale
              mockDays={mockDays}
              selectedWeek={selectedWeek}
              setSelectedWeek={setSelectedWeek}
              collaboratorsList={collaboratorsList}
              turniData={turniData}
              managerFerieList={managerFerieList}
              isDirector={isDirector}
              getInitials={getInitials}
              getBadgeProps={getBadgeProps}
              handleTimbra={handleTimbra}
            />
          )}
        </Pane>
      </Pane>

      {/* 3. MODALI */}
      <TurniDayDetailModal
        isOpen={isDayModalOpen}
        onClose={() => setIsDayModalOpen(false)}
        selectedDay={selectedDay}
        isDirector={isDirector}
        onRemoveShift={async (id) => {
          try {
            await handleDeleteTurno(id);
            setIsDayModalOpen(false);
          } catch (_e) { /* toaster già mostrato in handleDeleteTurno */ }
        }}
        onAssignClick={(dateStr) => {
          setIsDayModalOpen(false);
          if (isDirector) {
            setShiftInputs(prev => ({ ...prev, date: dateStr }));
            setIsTurnoModalOpen(true);
          }
        }}
      />

      <TurniShiftModal
        isOpen={isTurnoModalOpen}
        onClose={() => setIsTurnoModalOpen(false)}
        onSave={async () => { const ok = await handleSaveTurno(); if (ok) setIsTurnoModalOpen(false); }}
        isDirector={isDirector}
        shiftInputs={shiftInputs}
        setShiftInputs={setShiftInputs}
        userName={userName}
        collaboratorsList={collaboratorsList}
        turniData={turniData}
      />

      <TurniFerieRequestModal
        isOpen={isFerieModalOpen}
        onClose={() => setIsFerieModalOpen(false)}
        onRequest={async () => { const ok = await handleRequestFerie(); if (ok) setIsFerieModalOpen(false); }}
        ferieInputs={ferieInputs}
        setFerieInputs={setFerieInputs}
      />

      <TurniManagerFerieModal
        isOpen={isManagerFerieOpen}
        onClose={() => setIsManagerFerieOpen(false)}
        managerFerieList={managerFerieList}
        onUpdateStatus={handleUpdateFerieStatus}
        isDirector={isDirector}
      />
    </Pane>
  );
}
